import type { UseFormReturn } from "react-hook-form";

import { buildKudosDraftContext } from "@/features/kudos/components/create-kudos-dialog.helpers";
import type { CreateKudosInput } from "@/features/kudos/schemas/kudos.schema";
import type { MultiUserPickerOption } from "@/shared/components/multi-user-picker/multi-user-picker-list";
import { useWritingAssist } from "@/shared/writing-assistant/use-writing-assist";
import { useWritingAssistGate } from "@/shared/writing-assistant/use-writing-assist-gate";

/// Wires the kudos compose form into the shared writing assistant: rewrites
/// the `message` field in place, builds the recipient-names + value-tags
/// context, and reports whether the caller may use the feature (permission
/// AND AI credits, see `useWritingAssistGate`). Kept out of the dialog so
/// the dialog file stays under A3's 250-line ceiling.
export function useKudosWritingAssist(
  form: UseFormReturn<CreateKudosInput>,
  workspaceId: string,
  recipientById: ReadonlyMap<string, MultiUserPickerOption>,
) {
  const { canAssist, disabledReason } = useWritingAssistGate();
  const assist = useWritingAssist({
    workspaceId,
    surface: "kudos",
    getValue: () => form.getValues("message") ?? "",
    setValue: (text) => {
      // Kudos title is intentionally unused (spec decision 5); only the body
      // flows into the message field. The hook already writes draft.body.
      form.setValue("message", text, { shouldDirty: true, shouldValidate: true });
      form.setFocus("message");
    },
    buildContext: () =>
      buildKudosDraftContext(
        form.getValues("recipientUserIds") ?? [],
        recipientById,
        (form.getValues("valueTags") ?? []) as string[],
      ),
  });
  return { canAssist, disabledReason, assist };
}
